import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, PhoneOff, Clock, User, Briefcase, MapPin, PhoneCall, FileText, Wifi, WifiOff, Mic, MicOff } from 'lucide-react';
import { useToast } from '../components/ui/Toast';
import { CallScriptViewer } from '../components/calls/CallScriptViewer';
import { apiClient } from '../lib/api/client';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
// GenerateReportButton removed from this page - use Call History page instead
import { ExotelWebSDKService, CallSession } from '../services/exotelWebSDKService';
import { IncomingCallModal } from '../components/calls/IncomingCallModal';
import { CallStatusBar } from '../components/calls/CallStatusBar';
import { notificationService } from '../services/notificationService';

interface CallScriptPageProps {}

interface ScriptData {
  candidate_data: {
    name: string;
    phone: string;
    email?: string;
    location?: string;
  };
  company_data: {
    name: string;
    role_title: string;
    employment_type: string;
    location: string;
    key_benefits: string[];
    company_description: string;
    key_skills: string[];
  };
  recruiter_data: {
    name: string;
    phone: string;
  };
  script_data: {
    company_research: {
      company_description: string;
      key_benefits: string[];
      company_culture: string;
      recent_achievements: string[];
      why_work_here: string[];
      company_size: string;
      industry: string;
      location: string;
    };
    script_phases: Array<{
      id: string;
      name: string;
      duration: number;
      content: string;
      formatted_content?: string;
      questions: string[];
      is_required: boolean;
    }>;
  };
  total_phases: number;
  current_phase?: number;
  status?: string;
}

export const CallScriptPage: React.FC<CallScriptPageProps> = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [scriptData, setScriptData] = useState<ScriptData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [showPostCallActions, setShowPostCallActions] = useState(false);
  
  // VOIP State
  const [voipStatus, setVoipStatus] = useState<'disconnected' | 'connecting' | 'registered' | 'failed'>('disconnected');
  const [voipInitialized, setVoipInitialized] = useState(false);
  const [isVoipCall, setIsVoipCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const voipServiceRef = useRef<ExotelWebSDKService | null>(null);

  // Incoming Call State
  const [incomingCall, setIncomingCall] = useState<{
    callerNumber: string;
    candidateName?: string;
    candidateId?: string;
    callSid?: string;
  } | null>(null);

  // Call Status for Status Bar
  const [callStatus, setCallStatus] = useState<{
    callState: 'idle' | 'ringing' | 'active' | 'incoming' | 'ended';
    duration: number;
    remoteNumber?: string;
    candidateName?: string;
  }>({
    callState: 'idle',
    duration: 0
  });

  // Initialize VOIP WebSDK on component mount
  useEffect(() => {
    const initVoip = async () => {
      try {
        setVoipStatus('connecting');
        console.log('[CALL] 📞 Initializing Exotel WebSDK...');
        
        // Get WebSDK token from backend
        const tokenData = await apiClient.getVoipWebSDKToken();
        console.log('[CALL] ✅ WebSDK token retrieved');
        
        // Create and initialize Exotel WebSDK service
        const voipService = new ExotelWebSDKService();
        voipServiceRef.current = voipService;
        
        const success = await voipService.initialize(
          tokenData.access_token,
          tokenData.app_user_id,
          (status: CallSession) => {
            console.log('[CALL] 📞 Call status:', status);
            setCallDuration(status.duration);
            
            // Update UI based on call status
            if (status.status === 'active') {
              setIsCallActive(true);
              setIsVoipCall(true);
            } else if (status.status === 'ended' || status.status === 'failed') {
              setIsCallActive(false);
              setIsVoipCall(false);
              setIsMuted(false);
              if (status.status === 'ended') {
                setShowPostCallActions(true);
              }
            }
          }
        );
        
        if (success) {
          setVoipStatus('registered');
          setVoipInitialized(true);
          console.log('[CALL] ✅ Exotel WebSDK initialized successfully');
          
          // Request notification permission
          notificationService.requestPermission();
          
          // Set incoming call handler
          voipService.setIncomingCallHandler((data) => {
            console.log('[CallScriptPage] 📞 Incoming call:', data);
            setIncomingCall(data);
            
            // Update call status
            setCallStatus({
              callState: 'incoming',
              duration: 0,
              remoteNumber: data.callerNumber,
              candidateName: data.candidateName
            });
            
            // Show browser notification
            notificationService.showIncomingCall(
              data.callerNumber,
              data.candidateName,
              () => window.focus()
            );
          });
        } else {
          setVoipStatus('failed');
          console.error('[CALL] ❌ Exotel WebSDK initialization failed');
        }
      } catch (error: any) {
        console.error('[CALL] ❌ Failed to initialize Exotel WebSDK:', error);
        setVoipStatus('failed');
        // Don't show error toast - just silently fail and hide web call button
      }
    };

    initVoip();

    // Cleanup on unmount
    return () => {
      if (voipServiceRef.current) {
        voipServiceRef.current.destroy();
        voipServiceRef.current = null;
      }
    };
  }, []);

  // Call duration tracker for VOIP calls
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isCallActive && isVoipCall && voipServiceRef.current) {
      interval = setInterval(() => {
        if (voipServiceRef.current) {
          const duration = voipServiceRef.current.getCallDuration();
          setCallDuration(duration);
          setCallStatus(prev => ({ ...prev, duration }));
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isCallActive, isVoipCall]);

  // Incoming call handlers
  const handleAcceptCall = async () => {
    if (!incomingCall || !voipServiceRef.current) return;
    
    console.log('[CallScriptPage] ✅ Accepting call');
    voipServiceRef.current.acceptCall();
    
    // Log acceptance
    try {
      await apiClient.post(`/recruiter/voip-call-accept/${incomingCall.callSid}`);
    } catch (error) {
      console.warn('[CallScriptPage] Could not log accept:', error);
    }
    
    // Update status
    setIsCallActive(true);
    setIsVoipCall(true);
    setCallStatus({
      callState: 'active',
      duration: 0,
      remoteNumber: incomingCall.callerNumber,
      candidateName: incomingCall.candidateName
    });
    
    // Close modal
    setIncomingCall(null);
    notificationService.closeNotification();
  };

  const handleRejectCall = async () => {
    if (!incomingCall || !voipServiceRef.current) return;
    
    console.log('[CallScriptPage] ❌ Rejecting call');
    voipServiceRef.current.rejectCall();
    
    // Log rejection
    try {
      await apiClient.post(`/recruiter/voip-call-reject/${incomingCall.callSid}`);
    } catch (error) {
      console.warn('[CallScriptPage] Could not log reject:', error);
    }
    
    // Update status
    setCallStatus({
      callState: 'idle',
      duration: 0
    });
    
    // Close modal
    setIncomingCall(null);
    notificationService.closeNotification();
  };

  const handleEndCallFromStatusBar = () => {
    if (voipServiceRef.current) {
      voipServiceRef.current.endCall();
    }
    setIsCallActive(false);
    setIsVoipCall(false);
    setIsMuted(false);
    setCallStatus({
      callState: 'idle',
      duration: 0
    });
  };

  useEffect(() => {
    if (sessionId) {
      fetchScriptData();
    }
  }, [sessionId]);

  const fetchScriptData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[CALL] 🔍 CallScriptPage: Looking for sessionStorage key:', `call_script_${sessionId}`);
      console.log('[CALL] 🔍 CallScriptPage: Available sessionStorage keys:', Object.keys(sessionStorage));
      
      // Check if script data is available in sessionStorage
      const storedScriptData = sessionStorage.getItem(`call_script_${sessionId}`);
      if (storedScriptData) {
        console.log('[CALL] 🔍 CallScriptPage: Using stored script data from sessionStorage');
        const scriptData = JSON.parse(storedScriptData);
        setScriptData(scriptData);
        return;
      }
      
      // Fallback: Try to fetch analysis results from API if sessionStorage data is not available
      console.log('[CALL] 🔍 CallScriptPage: No sessionStorage data found, trying API fallback');
      
      const sessionParts = sessionId?.split('_');
      if (!sessionParts || sessionParts.length < 3) {
        throw new Error('Invalid session ID format');
      }
      
      const candidateId = sessionParts[1];
      const requirementId = sessionParts[2];
      
      console.log('[CALL] 🔍 CallScriptPage: Extracted IDs for API fallback:', { candidateId, requirementId });
      
      // Try to fetch the analysis results from the API
      const response = await apiClient.getCandidateAnalysisResults(candidateId, requirementId);
      
      if (response) {
        console.log('[CALL] 🔍 CallScriptPage: API fallback successful, analysis results:', response);
        
        // Check if call script data is available in the analysis results
        const analysisResults = response.analysis_results;
        const callScriptData = analysisResults?.cold_call_script;
        
        if (callScriptData) {
          console.log('[CALL] 🔍 CallScriptPage: Call script data found in API response');
          
          // Transform the data into the expected format
          const transformedScriptData = {
            candidate_data: {
              name: analysisResults?.form_data?.name || 'Candidate',
              phone: analysisResults?.form_data?.phone || '',
              email: analysisResults?.form_data?.email || '',
              location: analysisResults?.form_data?.location || ''
            },
            company_data: {
              name: 'Company', // This would need to be fetched from requirement data
              role_title: 'Position',
              employment_type: 'Full-time',
              location: '',
              key_benefits: [],
              company_description: '',
              key_skills: []
            },
            recruiter_data: {
              name: 'Recruiter',
              phone: ''
            },
            script_data: {
              company_research: {
                company_description: '',
                key_benefits: [],
                company_culture: '',
                recent_achievements: [],
                why_work_here: [],
                company_size: '',
                industry: '',
                location: ''
              },
              script_phases: [
                {
                  id: '1',
                  name: 'Opening & Introduction',
                  duration: 60,
                  content: callScriptData.opening || '',
                  questions: ['May I have a moment of your time?', 'Are you open to discussing new opportunities?'],
                  is_required: true
                },
                {
                  id: '2',
                  name: 'Discovery & Qualification',
                  duration: 180,
                  content: 'Understand candidate\'s current situation and qualifications',
                  questions: callScriptData.discovery || [],
                  is_required: true
                },
                {
                  id: '3',
                  name: 'Value Proposition & Next Steps',
                  duration: 120,
                  content: callScriptData.value_proposition || '',
                  questions: [],
                  is_required: true
                }
              ]
            },
            total_phases: 3
          };
          
          setScriptData(transformedScriptData);
          return;
        } else {
          console.log('[CALL] 🔍 CallScriptPage: No call script data found in API response');
        }
      }
      
      // If no data found in either sessionStorage or API, show error
      setError('Call script data not found. Please navigate to this page from the JD vs CV analysis page to access the call script.');
    } catch (err: any) {
      console.error('Error loading script data:', err);
      setError('Failed to load call script data. Please try navigating from the analysis page again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartCall = async () => {
    try {
      setIsCallActive(true);
      toast({
        title: "Call Starting",
        description: "Initiating call with candidate...",
        variant: "success",
        duration: 3000
      });
      
      // Extract candidate and requirement IDs from sessionId
      const sessionIdParts = sessionId?.split('_');
      if (!sessionIdParts || sessionIdParts.length < 3) {
        throw new Error('Invalid session ID format');
      }
      
      const candidateId = sessionIdParts[1];
      const requirementId = sessionIdParts[2];
      
      console.log('[CALL] 🔍 CallScriptPage: Starting call with data:', {
        candidate_phone: scriptData?.candidate_data?.phone,
        candidate_name: scriptData?.candidate_data?.name,
        candidate_id: candidateId,
        requirement_id: requirementId
      });

      // Call the actual start cold call API
      console.log('[CALL] 🔍 CallScriptPage: Making API call to startColdCall with:', {
        candidate_phone: scriptData?.candidate_data?.phone || '',
        candidate_id: candidateId,
        requirement_id: requirementId
      });
      
      const callResponse = await apiClient.startColdCall({
        candidate_phone: scriptData?.candidate_data?.phone || '',
        candidate_id: candidateId,
        requirement_id: requirementId
      });

      console.log('[CALL] 🔍 CallScriptPage: Call API response:', callResponse);

      if (callResponse.success) {
        toast({
          title: "Call Initiated",
          description: callResponse.message || "You will receive a call shortly.",
          variant: "success",
          duration: 5000
        });
      } else {
        throw new Error(callResponse.message || 'Failed to initiate call');
      }
    } catch (err: any) {
      console.error('Error starting call:', err);
      setIsCallActive(false);
      
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to initiate call. Please try again.';
      
      toast({
        title: "Call Failed",
        description: errorMessage,
        variant: "error",
        duration: 5000
      });
    }
  };

  const handleWebCall = async () => {
    try {
      if (!voipInitialized || voipStatus !== 'registered') {
        toast({
          title: "VOIP Not Ready",
          description: "Web calling is not available. Please use regular phone call or try again later.",
          variant: "error",
          duration: 5000
        });
        return;
      }

      setIsCallActive(true);
      setIsVoipCall(true);
      
      toast({
        title: "Starting Web Call",
        description: "Connecting via browser...",
        variant: "success",
        duration: 3000
      });
      
      // Extract candidate and requirement IDs from sessionId
      const sessionIdParts = sessionId?.split('_');
      if (!sessionIdParts || sessionIdParts.length < 3) {
        throw new Error('Invalid session ID format');
      }
      
      const candidateId = sessionIdParts[1];
      const requirementId = sessionIdParts[2];
      const candidatePhone = scriptData?.candidate_data?.phone || '';
      const candidateName = scriptData?.candidate_data?.name;

      console.log('[CALL] 📞 Starting VOIP call to:', candidatePhone);
      
      // Update call status
      setCallStatus({
        callState: 'ringing',
        duration: 0,
        remoteNumber: candidatePhone,
        candidateName
      });

      // Initiate VOIP call using Exotel WebSDK
      if (!voipServiceRef.current) {
        throw new Error('VOIP service not initialized');
      }

      const customField = JSON.stringify({
        candidate_id: candidateId,
        requirement_id: requirementId,
        session_id: sessionId
      });

      // Set call context for proper backend notification on call end
      voipServiceRef.current.setCallContext(candidateId, requirementId);

      const result = await voipServiceRef.current.makeCall(candidatePhone, customField);

      if (result.success) {
        toast({
          title: "Web Call Initiated",
          description: "Calling candidate via browser. You'll hear ringing shortly...",
          variant: "success",
          duration: 5000
        });

        // Log the VOIP call to backend
        await apiClient.logVoipCall({
          candidate_id: candidateId,
          requirement_id: requirementId,
          session_id: sessionId || '',
          voip_session_id: result.callSid,
          candidate_phone: candidatePhone
        });
      } else {
        throw new Error(result.message || 'Failed to initiate VOIP call');
      }
    } catch (err: any) {
      console.error('[CALL] ❌ Error starting web call:', err);
      setIsCallActive(false);
      setIsVoipCall(false);
      
      toast({
        title: "Web Call Failed",
        description: err?.message || 'Failed to initiate web call. Please try regular phone call.',
        variant: "error",
        duration: 5000
      });
    }
  };

  const handleEndCall = () => {
    if (isVoipCall && voipServiceRef.current) {
      voipServiceRef.current.hangup();
      setIsVoipCall(false);
    }
    
    setIsCallActive(false);
    setCurrentPhase(0);
    setShowPostCallActions(true);
    setCallDuration(0);
    setIsMuted(false);
    
    toast({
      title: "Call Ended",
      description: "Call session completed successfully. What would you like to do next?",
      variant: "success",
      duration: 5000
    });
  };

  const handleToggleMute = () => {
    if (isVoipCall && voipServiceRef.current) {
      voipServiceRef.current.toggleMute();
      setIsMuted(!isMuted);
      
      toast({
        title: isMuted ? "Microphone Unmuted" : "Microphone Muted",
        description: isMuted ? "Your microphone is now active" : "Your microphone is now muted",
        variant: "success",
        duration: 2000
      });
    } else {
      toast({
        title: "Mute Not Available",
        description: "Mute is only available for web calls",
        variant: "error",
        duration: 2000
      });
    }
  };

  // Screening functions and extractIds removed - no longer needed
  // Generate report button directly available in call history page

  const handleGoToCallHistory = () => {
    const sessionParts = sessionId?.split('_');
    if (sessionParts && sessionParts.length >= 3) {
      const candidateId = sessionParts[1];
      navigate(`/candidates/${candidateId}/calls`);
    }
  };


  const handleBackToCandidate = () => {
    navigate(-1); // Go back to previous page
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-enterprise-primary via-background-enterprise-secondary to-background-enterprise-primary relative overflow-hidden">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <LoadingSpinner size="xl" variant="ai" className="mx-auto mb-4" />
            <p className="text-text-enterprise-secondary">Loading call script...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-enterprise-primary via-background-enterprise-secondary to-background-enterprise-primary relative overflow-hidden">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PhoneOff className="w-8 h-8 text-error-500" />
            </div>
            <h2 className="text-xl font-semibold text-text-enterprise-primary mb-2">Failed to Load Script</h2>
            <p className="text-text-enterprise-secondary mb-4">{error}</p>
            <div className="flex gap-3">
              <button
                onClick={handleBackToCandidate}
                className="px-4 py-2 bg-primary-500 text-white dark:text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Go Back
              </button>
              {error.includes('analysis') && (
                <button
                  onClick={() => {
                    const sessionParts = sessionId?.split('_');
                    if (sessionParts && sessionParts.length >= 3) {
                      const candidateId = sessionParts[1];
                      const requirementId = sessionParts[2];
                      navigate(`/candidates/${candidateId}/analysis/${requirementId}`);
                    }
                  }}
                  className="px-4 py-2 bg-cyan-500 text-white dark:text-white rounded-lg hover:bg-cyan-600 transition-colors"
                >
                  Run Analysis
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!scriptData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-enterprise-primary via-background-enterprise-secondary to-background-enterprise-primary relative overflow-hidden">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="w-8 h-8 text-warning-500" />
            </div>
            <h2 className="text-xl font-semibold text-text-enterprise-primary mb-2">No Script Available</h2>
            <p className="text-text-enterprise-secondary mb-4">Call script not found for this session.</p>
            <button
              onClick={handleBackToCandidate}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalDuration = scriptData.script_data?.script_phases?.reduce((total, phase) => total + phase.duration, 0) || 0;
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-[#0a0f1c] relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-[#0f172a] to-[#0a0f1c]"></div>
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-cyan-500/8 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-violet-500/8 rounded-full blur-[100px]"></div>
        <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-emerald-500/5 rounded-full blur-[80px]"></div>
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]"></div>
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToCandidate}
                className="p-2.5 bg-slate-800/80 backdrop-blur-xl rounded-xl border border-slate-700/60 hover:bg-slate-700/80 hover:border-slate-600 transition-all duration-200 shadow-lg"
              >
                <ArrowLeft className="w-5 h-5 text-slate-200" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <span className="text-2xl">📞</span>
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Cold Call Script</span>
                </h1>
                <p className="text-slate-400 mt-0.5">AI-generated script for your call</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Persistent Call History Button */}
              <button
                onClick={handleGoToCallHistory}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl font-medium transition-all duration-200 flex items-center gap-2 shadow-lg shadow-blue-500/20 border border-blue-500/30"
              >
                <PhoneCall className="w-4 h-4" />
                Call History & Reports
              </button>
              
              <div className="flex items-center gap-1.5 ml-2">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Candidate Info Card */}
        <div className="mb-8">
          <div className="bg-slate-800/60 backdrop-blur-2xl rounded-2xl border border-slate-700/60 shadow-2xl shadow-black/20 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/30 to-blue-600/30 rounded-xl flex items-center justify-center border border-cyan-500/30 shadow-lg shadow-cyan-500/10">
                  <User className="w-7 h-7 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    {scriptData.candidate_data?.name || 'Candidate'}
                  </h2>
                  <div className="flex items-center gap-5 mt-1.5 text-sm text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <Briefcase className="w-4 h-4 text-slate-500" />
                      <span>{scriptData.company_data?.role_title || 'Position'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      <span>{scriptData.company_data?.location || 'Location'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <span>{formatDuration(totalDuration)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-right px-4 py-3 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <div className="text-xs text-slate-500 uppercase tracking-wider font-medium">Total Duration</div>
                <div className="text-2xl font-bold text-white mt-1">
                  {formatDuration(totalDuration)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Script Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Script Viewer */}
          <div className="lg:col-span-2">
            <CallScriptViewer
              scriptData={scriptData}
              currentPhase={currentPhase}
              onPhaseChange={setCurrentPhase}
              isCallActive={isCallActive}
            />
          </div>

          {/* Call Controls */}
          <div className="lg:col-span-1">
            <div className="bg-slate-800/60 backdrop-blur-2xl rounded-2xl border border-slate-700/60 shadow-2xl shadow-black/20 p-6 space-y-4">
              {/* VOIP Status Indicator */}
              {voipStatus !== 'disconnected' && (
                <div className={`flex items-center justify-between gap-2 px-4 py-3 rounded-xl border ${
                  voipStatus === 'registered' 
                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                    : voipStatus === 'connecting' 
                    ? 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center gap-2.5">
                    {voipStatus === 'registered' ? (
                      <Wifi className="w-4 h-4 text-emerald-400" />
                    ) : voipStatus === 'connecting' ? (
                      <Wifi className="w-4 h-4 text-amber-400 animate-pulse" />
                    ) : (
                      <WifiOff className="w-4 h-4 text-red-400" />
                    )}
                    <span className={`text-sm font-medium ${
                      voipStatus === 'registered' 
                        ? 'text-emerald-300' 
                        : voipStatus === 'connecting' 
                        ? 'text-amber-300'
                        : 'text-red-300'
                    }`}>
                      {voipStatus === 'registered' ? 'Web calling ready' :
                       voipStatus === 'connecting' ? 'Connecting...' :
                       'Web calling unavailable'}
                    </span>
                  </div>
                  {voipStatus === 'registered' && (
                    <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-400/50"></div>
                  )}
                </div>
              )}

              {/* Call Duration (for VOIP calls) */}
              {isCallActive && isVoipCall && (
                <div className="text-center py-5 px-4 bg-gradient-to-br from-cyan-500/15 to-blue-600/15 rounded-xl border border-cyan-500/30">
                  <div className="text-xs text-slate-400 uppercase tracking-wider font-medium">Call Duration</div>
                  <div className="text-4xl font-bold text-white mt-2 font-mono">
                    {formatDuration(callDuration)}
                  </div>
                </div>
              )}

              {!isCallActive ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Start Your Call</h3>
                    <p className="text-sm text-slate-400 mt-1">
                      Choose how you'd like to connect with the candidate
                    </p>
                  </div>

                  {/* Web Call Button (VOIP) */}
                  {voipInitialized && voipStatus === 'registered' && (
                    <button
                      onClick={handleWebCall}
                      className="w-full px-4 py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2.5 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <Wifi className="w-5 h-5" />
                      <span>Web Call (Browser)</span>
                    </button>
                  )}

                  {/* Regular Phone Call Button (PSTN) */}
                  <button
                    onClick={handleStartCall}
                    className="w-full px-4 py-3.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2.5 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <Phone className="w-5 h-5" />
                    <span>Phone Call (Traditional)</span>
                  </button>

                  <div className="mt-5 pt-5 border-t border-slate-700/60">
                    <div className="space-y-2.5 text-sm text-slate-400">
                      <p className="flex items-start gap-2">
                        <span className="text-base">💻</span>
                        <span><strong className="text-slate-300">Web Call:</strong> Call from browser, no phone needed</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-base">📞</span>
                        <span><strong className="text-slate-300">Phone Call:</strong> You'll receive a call on your registered number</span>
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500/20 to-green-600/20 rounded-2xl mb-4 border border-emerald-500/30 shadow-lg shadow-emerald-500/10">
                      <Phone className="w-10 h-10 text-emerald-400 animate-pulse" />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      {isVoipCall ? 'Web Call Active' : 'Call Active'}
                    </h3>
                    <p className="text-sm text-emerald-400 mt-1 font-medium">
                      {isVoipCall ? 'Connected via browser' : 'Connected via phone'}
                    </p>
                  </div>

                  {/* VOIP Controls - Mute/Unmute */}
                  {isVoipCall && (
                    <button
                      onClick={handleToggleMute}
                      className={`w-full px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                        isMuted
                          ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40'
                          : 'bg-slate-700/60 hover:bg-slate-700/80 text-slate-200 border border-slate-600/50'
                      }`}
                    >
                      {isMuted ? (
                        <>
                          <MicOff className="w-5 h-5" />
                          <span>Unmute Microphone</span>
                        </>
                      ) : (
                        <>
                          <Mic className="w-5 h-5" />
                          <span>Mute Microphone</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* End Call Button */}
                  <button
                    onClick={handleEndCall}
                    className="w-full px-4 py-3.5 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-500/25 hover:shadow-red-500/40"
                  >
                    <PhoneOff className="w-5 h-5" />
                    <span>End Call</span>
                  </button>

                  {/* Phase Progress */}
                  <div className="mt-4 pt-4 border-t border-slate-700/60">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-slate-400">Script Progress</span>
                      <span className="text-sm font-bold text-white">
                        {currentPhase + 1} / {scriptData.total_phases || 0}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700/60 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-cyan-400 to-blue-500 h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${((currentPhase + 1) / (scriptData.total_phases || 1)) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Post-Call Actions */}
            {showPostCallActions && !isCallActive && (
              <div className="mt-6 bg-gradient-to-br from-emerald-500/10 to-green-600/10 backdrop-blur-2xl rounded-2xl border border-emerald-500/30 shadow-2xl shadow-emerald-500/10 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center border border-emerald-500/30">
                    <PhoneOff className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">
                    Call Completed
                  </h3>
                </div>
                
                <p className="text-sm text-slate-300 mb-5">
                  What would you like to do next?
                </p>

                <div className="space-y-3">
                  <button
                    onClick={handleGoToCallHistory}
                    className="w-full px-4 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
                  >
                    <PhoneCall className="w-4 h-4" />
                    View Call History & Generate Reports
                  </button>

                  <button
                    onClick={() => {
                      const sessionParts = sessionId?.split('_');
                      if (sessionParts && sessionParts.length >= 2) {
                        const candidateId = sessionParts[1];
                        navigate(`/candidates/${candidateId}`);
                      }
                    }}
                    className="w-full px-4 py-3 bg-slate-700/60 hover:bg-slate-700/80 text-slate-200 border border-slate-600/50 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Back to Candidate Profile
                  </button>

                  <button
                    onClick={handleBackToCandidate}
                    className="w-full px-4 py-2.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    Close
                  </button>
                </div>

                <div className="mt-5 pt-4 border-t border-slate-700/60">
                  <p className="text-sm text-slate-400 flex items-start gap-2">
                    <span className="text-base">💡</span>
                    <span><strong className="text-slate-300">Tip:</strong> From Call History, you can transcribe this call, view individual analysis, or merge multiple calls for consolidated reports.</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Incoming Call Modal */}
      {incomingCall && (
        <IncomingCallModal
          callerNumber={incomingCall.callerNumber}
          candidateName={incomingCall.candidateName}
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
        />
      )}

      {/* Call Status Bar */}
      {callStatus.callState !== 'idle' && (
        <CallStatusBar
          callState={callStatus.callState}
          duration={callStatus.duration}
          remoteNumber={callStatus.remoteNumber}
          candidateName={callStatus.candidateName}
          isMuted={isMuted}
          onMuteToggle={() => {
            if (voipServiceRef.current) {
              const newMutedState = !isMuted;
              voipServiceRef.current.toggleMute();
              setIsMuted(newMutedState);
            }
          }}
          onHangup={handleEndCallFromStatusBar}
        />
      )}
    </div>
  );
};
